import React, { Fragment, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";

import { putDataWithDotNet } from "../../../../newApis/dotNetApiMethods";
import * as ajaxEndpoints from "../../../../api/ajax-endpoints";
import Alert from "../../../../NewComponents/Alert/Alert.component";
import { errorHandler } from "../../../../helpers/errorHandler";
import { ROLE_TYPES } from "../../../../helpers/roleTypes";
import { appInsights } from "../../../../config/appInsights";
import { RootState } from "../../../../redux/store";
import useAnInvestmentProduct from "../../../../custom-hooks/useAnInvestmentProduct";
import { getCurrencyInfo } from "../../helper";
import { resolveProductVisibility } from "../../components/ProductVisibility";
import CreateInvestmentModal from "../CreateInvestmentModal/CreateInvestmentModal";
import FormSection from "../FundForm/FormSection";
import { needsWithdrawalWindows } from "../FundForm/planType";
import { Check, ChevronRight, Clock, Details, Pencil } from "../FundForm/icons";
import DetailList from "./components/DetailList";
import GlanceStrip from "./components/GlanceStrip";
import VisibilityDocumentsCard from "./components/VisibilityDocumentsCard";
import WithdrawalWindowsCard from "./components/WithdrawalWindowsCard";
import CurrencyFinancialsCard from "./components/CurrencyFinancialsCard";
import { formatDate, planTypeName, TSavedFund } from "./previewFund";
import formStyles from "../FundForm/fundForm.module.scss";
import styles from "./previewFund.module.scss";

type TRailSection = { id: string; label: string };

/** How long the activation success message shows before going back. */
const RETURN_AFTER_ACTIVATION_MS = 4000;

/**
 * A drafted fund, read back before it is activated.
 *
 * The read-only twin of Create / Edit Fund: the same action bar, rail and
 * section cards, so what was entered is laid out the way it was entered. The
 * fund comes from the id in the URL, so a refresh keeps the page.
 */
export default function PreviewInvesmentFunds() {
  const { id: routeId } = useParams<{ id: string }>();
  const location = useLocation<{ data?: { id?: number } } | undefined>();
  const fundId = Number(routeId ?? location.state?.data?.id);

  const history = useHistory();
  const dismissModalRef = useRef<HTMLButtonElement>(null);
  const [modalActionType, setModalActionType] = useState("");
  const [activating, setActivating] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [activateState, setActivateState] = useState({ otp: "", comment: "" });
  const [activeSection, setActiveSection] = useState("details");

  const roles =
    useSelector(
      (state: RootState) => state?.authReducer?.loggedInUser?.roles,
    ) ?? [];
  const canEdit = roles.includes(ROLE_TYPES.INVESTMENT_PRODUCT_SETUP);
  const canActivate = roles.includes(ROLE_TYPES.INVESTMENT_PRODUCT_APPROVER);

  const { data, status } = useAnInvestmentProduct(fundId);
  const fund: TSavedFund | undefined = data;

  useEffect(() => {
    appInsights.trackPageView({
      name: "Preview Investment Fund - (PreviewInvesmentFunds.tsx)",
      isLoggedIn: true,
    });
  }, []);

  const closeActivateModal = () => dismissModalRef.current?.click();

  const handleActivate = async (e: React.FormEvent) => {
    e.preventDefault();
    setActivating(true);
    try {
      const response = await putDataWithDotNet(
        `${ajaxEndpoints.FUNDER_INVESTMENT_PRODUCT}/${fundId}/activate`,
        activateState,
      );
      closeActivateModal();
      setSuccessMessage(response.message);
      setTimeout(
        () => history.push("/investments"),
        RETURN_AFTER_ACTIVATION_MS,
      );
    } catch (error) {
      appInsights.trackException({
        exception: error as Error,
        properties: { fileName: "PreviewInvesmentFunds.tsx" },
      });
      closeActivateModal();
      setActivateError(errorHandler(error));
    } finally {
      setActivating(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setActivateState((prev) => ({ ...prev, [name]: value }));
  };

  const discardChanges = (e: React.FormEvent) => {
    e.preventDefault();
    closeActivateModal();
  };

  const details = fund?.investment_product_details ?? [];
  const hasWindows = needsWithdrawalWindows(fund?.product_type_id?.value);
  const isVisible = fund ? resolveProductVisibility(fund) : false;

  const sections: TRailSection[] = [
    { id: "details", label: "Investment details" },
    { id: "visibility", label: "Visibility & documents" },
    ...(hasWindows
      ? [{ id: "withdrawal-windows", label: "Withdrawal windows" }]
      : []),
    ...details.map((detail) => ({
      id: `financials-${detail.currency_code}`,
      label: `Financials · ${
        getCurrencyInfo(detail.currency_code)?.name ?? detail.currency_code
      }`,
    })),
  ];
  const stepOf = (id: string) =>
    `Step ${sections.findIndex((section) => section.id === id) + 1} of ${
      sections.length
    }`;

  const goTo = (id: string) => {
    setActiveSection(id);
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Fragment>
      <div className={formStyles.page}>
        <div className={formStyles.actionBar}>
          <div style={{ minWidth: 0 }}>
            <nav aria-label="Breadcrumb" className={formStyles.crumbs}>
              <Link to="/investments" style={{ color: "inherit" }}>
                Investments
              </Link>
              <ChevronRight color="#8b8699" />
              <span>Preview fund</span>
            </nav>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 12,
              }}
            >
              <h1 className={formStyles.pageTitle}>
                {fund?.name ?? "Preview fund"}
              </h1>
              {fund ? (
                <span
                  className={formStyles.statusPill}
                  style={{ marginTop: 4 }}
                >
                  <Clock color="#713fff" />
                  Draft · awaiting activation
                </span>
              ) : null}
            </div>
          </div>

          {fund ? (
            <div className={formStyles.actions}>
              {canEdit ? (
                <Link
                  to={{
                    pathname: `/investments/${fundId}/edit`,
                    state: { editType: "drafted", id: fundId },
                  }}
                  className={`${styles.buttonOutline} ${styles.buttonLarge}`}
                >
                  <Pencil />
                  Edit fund
                </Link>
              ) : null}
              {canActivate ? (
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonLarge}`}
                  disabled={activating}
                  data-toggle="modal"
                  data-target="#createInvestmentModal"
                  onClick={() => setModalActionType("activate")}
                >
                  <Check size={16} />
                  Activate fund
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        {activateError || successMessage ? (
          <div style={{ padding: "16px 48px 0" }}>
            {activateError ? <Alert message={activateError} /> : null}
            {successMessage ? (
              <Alert message={successMessage} type="success" />
            ) : null}
          </div>
        ) : null}

        {status === "loading" ? (
          <div className={styles.state}>Loading the fund…</div>
        ) : !fund ? (
          <div className={styles.state}>
            We couldn't load this fund. Reload the page to try again.
          </div>
        ) : (
          <div className={formStyles.body}>
            <nav aria-label="Sections" className={formStyles.rail}>
              <div className={formStyles.railHeading}>Sections</div>
              {sections.map((section, index) => {
                const isActive = section.id === activeSection;

                return (
                  <button
                    key={section.id}
                    type="button"
                    className={`${formStyles.railItem} ${
                      isActive ? formStyles.railItemActive : ""
                    }`}
                    onClick={() => goTo(section.id)}
                  >
                    <span
                      className={`${formStyles.railStep} ${
                        isActive ? formStyles.railStepActive : ""
                      }`}
                    >
                      {index + 1}
                    </span>
                    {section.label}
                  </button>
                );
              })}
              <div className={formStyles.railDivider} />
              <div className={formStyles.railNote}>
                Read only. Edit opens the same form this fund was created with.
              </div>
            </nav>

            <main className={formStyles.column}>
              <GlanceStrip fund={fund} isVisible={isVisible} />

              <FormSection
                id="details"
                title="Investment details"
                subtitle="What this fund is and when it takes subscriptions."
                icon={<Details color="#713fff" />}
                meta={stepOf("details")}
              >
                <DetailList
                  items={[
                    { label: "Investment name", value: fund.name },
                    {
                      label: "Plan type",
                      value: planTypeName(fund) ?? "Not a savings plan",
                    },
                    {
                      label: "Investment type",
                      value: fund.investment_product_type_name,
                    },
                    {
                      label: "Subtype · detail",
                      value: [
                        fund.investment_product_sub_type_name,
                        fund.investment_product_sub_type_detail_name,
                      ]
                        .filter(Boolean)
                        .join(" · "),
                    },
                    {
                      label: "Subscription opens",
                      value: formatDate(fund.start_date),
                    },
                    {
                      label: "Subscription closes",
                      value: formatDate(fund.end_date),
                    },
                    {
                      label: "About this fund",
                      value: fund.description,
                      wide: true,
                    },
                    { label: "Objective", value: fund.objective },
                    { label: "Issuer", value: fund.issuers },
                    { label: "Target investors", value: fund.target_investors },
                  ]}
                />
              </FormSection>

              <VisibilityDocumentsCard
                fund={fund}
                isVisible={isVisible}
                step={stepOf("visibility")}
              />

              {hasWindows ? (
                <WithdrawalWindowsCard
                  fund={fund}
                  step={stepOf("withdrawal-windows")}
                />
              ) : null}

              {details.map((detail, index) => (
                <CurrencyFinancialsCard
                  key={detail.id ?? detail.currency_code}
                  detail={detail}
                  position={`${index + 1} of ${details.length} ${
                    details.length === 1 ? "currency" : "currencies"
                  }`}
                />
              ))}
            </main>
          </div>
        )}
      </div>

      <CreateInvestmentModal
        onSubmit={handleActivate}
        discardChanges={discardChanges}
        modalActionType={modalActionType}
        dismissModalRef={dismissModalRef}
        loading={activating}
        successMessage={successMessage}
        saveDraftError={activateError}
        handleChange={handleChange}
      />
    </Fragment>
  );
}
